import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';

import AudioClipEditor from '@/components/media/audio/AudioClipEditor.js';
import AudioPreview from '@/components/media/audio/AudioPreview.js';
import {
  decodeAudioFile,
  formatAudioTime,
} from '@/components/media/audio/audioClipUtils.js';

const AUDIO_ACCEPT = '.aac,.flac,.m4a,.mp3,.ogg,.opus,.wav,audio/*';

export const JianyingAudioSlotEditor = ({
  disabled,
  error,
  onChange,
  onError,
  registerFieldRef,
  slot,
  value,
}) => {
  const inputRef = useRef(null);
  const selectionRequestRef = useRef(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [reading, setReading] = useState(false);
  const maxDurationSeconds = Number(slot.config?.duration_us || 0) / 1_000_000;

  useEffect(() => () => {
    selectionRequestRef.current += 1;
  }, []);

  const applyFile = (file, durationSeconds) => {
    onChange({
      kind: 'audio',
      file,
      file_name: file.name,
      duration_us: Math.round(durationSeconds * 1_000_000),
    });
  };

  const selectFile = async (file) => {
    if (!file) return;
    const requestId = selectionRequestRef.current + 1;
    selectionRequestRef.current = requestId;
    setReading(true);
    try {
      const { durationSeconds } = await decodeAudioFile(file);
      if (selectionRequestRef.current !== requestId) return;
      if (durationSeconds > maxDurationSeconds + 0.05) {
        setPendingFile(file);
      } else {
        applyFile(file, durationSeconds);
      }
    } catch (error) {
      if (selectionRequestRef.current === requestId) onError?.(error);
    } finally {
      if (selectionRequestRef.current === requestId) {
        setReading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    }
  };

  return (
    <article className={`jianying-audio-slot ${error ? 'is-invalid' : ''}`.trim()}>
      <div className="jianying-audio-slot__heading">
        <div>
          <strong>{slot.description || slot.key}</strong>
          <span>最长 {formatAudioTime(maxDurationSeconds)}</span>
        </div>
        {value?.file_name ? (
          <span className="jianying-audio-slot__duration">
            {formatAudioTime(Number(value.duration_us) / 1_000_000)}
          </span>
        ) : null}
      </div>

      {value?.file ? (
        <AudioPreview
          file={value.file}
          label={`试听 ${slot.description || slot.key}`}
        />
      ) : null}
      {value?.file_name ? (
        <div className="jianying-audio-slot__file">
          <i className="bi bi-music-note-beamed" aria-hidden="true" />
          <span title={value.file_name}>{value.file_name}</span>
        </div>
      ) : (
        <div className="jianying-audio-slot__empty">尚未选择音频</div>
      )}

      <div className="jianying-audio-slot__actions">
        <input
          ref={inputRef}
          type="file"
          accept={AUDIO_ACCEPT}
          hidden
          disabled={disabled || reading}
          onChange={(event) => selectFile(event.target.files?.[0])}
        />
        <Button
          ref={(node) => registerFieldRef?.(slot.id, node)}
          type="button"
          size="sm"
          variant="outline-primary"
          disabled={disabled || reading}
          onClick={() => inputRef.current?.click()}
        >
          {reading ? <Spinner size="sm" aria-label="正在读取音频" /> : <i className="bi bi-folder2-open" />}
          <span>{value?.file_name ? '更换音频' : '选择音频'}</span>
        </Button>
        {value?.file ? (
          <Button
            type="button"
            size="sm"
            variant="outline-secondary"
            disabled={disabled}
            onClick={() => setPendingFile(value.file)}
          >
            <i className="bi bi-scissors" />
            <span>裁剪</span>
          </Button>
        ) : null}
        {value?.file_name ? (
          <Button
            type="button"
            size="sm"
            variant="outline-danger"
            aria-label={`移除 ${slot.description || slot.key}`}
            title="移除音频"
            disabled={disabled}
            onClick={() => onChange(undefined)}
          >
            <i className="bi bi-trash3" />
          </Button>
        ) : null}
      </div>
      {error ? (
        <div className="jianying-audio-slot__error" role="alert">{error}</div>
      ) : null}

      <Modal
        centered
        size="lg"
        show={Boolean(pendingFile)}
        onHide={() => setPendingFile(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>裁剪 {slot.description || slot.key}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingFile ? (
            <AudioClipEditor
              file={pendingFile}
              maxDurationSeconds={maxDurationSeconds}
              onCancel={() => setPendingFile(null)}
              onConfirm={({ file, durationSeconds }) => {
                applyFile(file, durationSeconds);
                setPendingFile(null);
              }}
              onError={onError}
            />
          ) : null}
        </Modal.Body>
      </Modal>
    </article>
  );
};
